import {
  Autocomplete,
  Box,
  Chip,
  FormControlLabel,
  FormLabel,
  Paper,
  Radio,
  RadioGroup,
  TableBody,
  TableCell,
  TableRow,
  TextField,
  Typography,
  TableContainer,
  Table,
  TablePagination,
  Pagination,
} from "@mui/material";
import { Stack } from "@mui/system";
import CancelIcon from "@mui/icons-material/Cancel";
import { CheckPermissions } from "../../../../logic/Admin/Permissions/CheckPermission/CheckPermissions";
import { useDispatch, useSelector } from "react-redux";
import {
  selectUserData,
  selectUserToken,
  setDisplayErrorSnackBar,
} from "../../../../features/userSlice";
import Page404 from "../../../Page404/Page403";
import { useLocation, useSearchParams } from "react-router-dom";
import { useContext, useEffect, useState, useMemo } from "react";
import { BusinessInsightsEndPointLogic } from "../../../../logic/AssetManagement/BusinessInsights/BusinessInsightsEndPointLogic";
import store from "../../../../store";
import {
  setBusinessInsightsList,
  setCreatedOnDate,
  setFilteredInsights,
  setSelectedAssignee,
  setSelectedBusinessInsight,
  setSelectedCategory,
  setSelectedDueDate,
  setSelectedSharedUid,
  setSelectedSite,
  setSelectedStatus,
  setSelectedSubCategory,
  setOpenModalType,
} from "../../../../features/businessInsightsSlice";
import SharedStateContext from "../../../SharedStateProvider";
import InsightsTable from "./InsightsTable/InsightsTable";
import InsightsFilters from "./InsightsFilters/InsightsFilters";
import SimpleBackdrop from "../../../BackDrop/BackDrop";
import FloatingActionItems from "../../../../pages/FloatingItems/FloatingActionItems";

interface SiteOption {
  value: string;
  label: string;
  site_name?: string;
  ui_site_name?: string;
}

const Insights = () => {
  const oauthTokenHeader = useSelector(selectUserToken) as any;
  const userData = useSelector(selectUserData);
  const dispatch = useDispatch();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { shouldRunEffect, setLoad } = useContext(SharedStateContext);
  const [, setSearchTerm] = useState("");
  const response = useSelector(selectUserData); // Get user data from Redux store
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(false);
  const [search, setSearch] = useState(false);
  const [dataShow, setDataShow] = useState(false);
  const [, setDataLoaded] = useState(false);
  const [, setSiteSelectionTrigger] = useState(0);
  const [, setTableRefresh] = useState(false);
  const { selectedSharedUid, businessInsightsList, filteredInsights } =
    useSelector((state: any) => state.businessInsights); // Get selected shared UID and business insights list from Redux store
  const [selectedOption, setSelectedOption] = useState<string | null>(null);
  const taskStructureShareUid = searchParams.get("taskStructureUid");
  const taskSharedUid = searchParams.get("taskSharedUid");
  const timeFrame = searchParams.get("timeFrame");
  const searchQuery = searchParams.get("search");
  const insightType = searchParams.get("insightType");

  useEffect(() => {
    if (
      taskStructureShareUid ||
      taskSharedUid ||
      timeFrame ||
      searchQuery ||
      insightType
    ) {
      const newSearchParams = new URLSearchParams(window.location.search); // Create a copy of the current search parameters
      newSearchParams.delete("taskStructureUid"); // Remove the taskStructureShareUid from the URL
      newSearchParams.delete("taskSharedUid"); // Remove the taskSharedUid from the URL
      newSearchParams.delete("timeFrame"); // Remove the timeFrame from the URL
      newSearchParams.delete("insightType"); // Remove the insightType query from the URL
      setSearchParams(newSearchParams.toString()); // Update the search parameters in the URL
      window.history.replaceState(
        {},
        "",
        `${location.pathname}?${newSearchParams}`
      ); // Replace the URL without reloading
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taskStructureShareUid, taskSharedUid]);

  useEffect(() => {
    if (!selectedSites) {
      setLoading(false);
      setSearch(false);
      setDataShow(false);
    }
    setLoad(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldRunEffect, selectedSharedUid, setLoad]);

  const SELECT_ALL_OPTION = { value: "select_all", label: "Select All" };
  const [selectedSites, setSelectedSites] = useState<any[]>([]);

  const siteOptions = useMemo(
    () =>
      userData?.data?.website_permissions?.map((siteObj: any) => {
        const uid = Object.keys(siteObj)[0]; // UID of the site
        const siteName = siteObj[uid].ui_site_name; // Site name
        return { uid, siteName, shared_uid: uid }; // Add shared_uid for consistency
      }) || [],
    [userData]
  );

  const [selectedSiteUids, setSelectedSiteUids] = useState<string[]>([]);

  const mappedSiteOptions =
    siteOptions.map((site: any) => ({
      value: site.uid,
      label: site.siteName,
    })) || [];

  // Derived variable to check if all sites are selected
  const allSitesSelected =
    selectedSites.length === mappedSiteOptions.length &&
    mappedSiteOptions.length > 0;

  //--Function to clear filters and clear query parameters
  const clearFilters = () => {
    setSearchParams({}); // clear all URL's query parameters
    dispatch(setSelectedBusinessInsight(null));
    dispatch(setSelectedCategory(null));
    dispatch(setSelectedSubCategory(null));
    dispatch(setCreatedOnDate(null));
    dispatch(setSelectedStatus(null));
    dispatch(setSelectedDueDate(null));
    dispatch(setSelectedAssignee(null));
    dispatch(setBusinessInsightsList([]));
  };
  const handleSelectSites = (_: any, selectedValues: any[]) => {
    setInitialLoading(true);
    setTableRefresh(true);
    const isSelectAll = selectedValues.some(
      (opt) => opt.value === SELECT_ALL_OPTION.value
    );

    if (isSelectAll) {
      if (selectedSites.length === mappedSiteOptions.length) {
        setSelectedSites([]);
        setSelectedSiteUids([]);
        setTableRefresh(true);
      } else {
        setSelectedSites(mappedSiteOptions);
        setSelectedSiteUids(mappedSiteOptions.map((site: any) => site.value));
      }
    } else {
      const uids = selectedValues.map((site) => site.value);
      setSelectedSites(selectedValues);
      setSelectedSiteUids(uids);
      setTableRefresh(true);
    }

    // Always update query params on selection
    const updatedParams = new URLSearchParams(searchParams);
    if (isSelectAll) {
      if (selectedSites.length === mappedSiteOptions.length) {
        updatedParams.delete("selectedSites");
      } else {
        // When selecting all, use all site UIDs
        updatedParams.set(
          "selectedSites",
          mappedSiteOptions
            .map((site: { value: string; label: string }) => site.value)
            .join(",")
        );
      }
    } else {
      if (selectedValues.length > 0) {
        updatedParams.set(
          "selectedSites",
          selectedValues.map((site) => site.value).join(",")
        );
      } else {
        updatedParams.delete("selectedSites");
        updatedParams.delete("search");
        updatedParams.delete("filters");
        clearFilters();
      }
    }
    setSearchParams(updatedParams);
    setSiteSelectionTrigger((prev) => prev + 1);

    // Set search and dataShow to true to trigger data loading
    if (selectedValues.length > 0) {
      setSearch(true);
      setDataShow(true);
      setTableRefresh(true);
    } else {
      setSearch(false);
      setDataShow(false);
      setInitialLoading(false);
      setTableRefresh(true);
    }
  };

  useEffect(() => {
    const siteUidsFromParams = searchParams.get("selectedSites");
    if (siteUidsFromParams) {
      const uidArray = siteUidsFromParams.split(",");
      const selected = mappedSiteOptions.filter((site: any) =>
        uidArray.includes(site.value)
      );
      setSelectedSites(selected); // Ensure exact option objects from `mappedSiteOptions`
      setSelectedSiteUids(uidArray);
      setSearch(true);
      setDataShow(true);
      setTableRefresh(true);
    }

    // eslint-disable-next-line
  }, [searchParams]);

  const hasViewBusinessInsightsPermission = CheckPermissions(
    response?.data?.products?.paid_traffic,
    "performance_analysis",
    "view_paid_traffic_insights"
  ); // Check if the user has permission to vie  w business insights

  // Reset state only when component unmounts, not on mount
  useEffect(() => {
    return () => {
      // Reset when component unmounts (navigating away)
      store.dispatch(setSelectedSharedUid(null));
      store.dispatch(setSelectedBusinessInsight(null));
      store.dispatch(setSelectedSite(null));
      setSelectedSites([]);
    };
  }, []);

  // below portion is for setting and deleting the site
  const updateURLParams = (siteUid: any) => {
    const updatedParams = new URLSearchParams(window.location.search); // Get existing params
    if (siteUid) {
      updatedParams.set("selectedSites", siteUid); // Update the value
    } else {
      updatedParams.delete("selectedSites"); // Remove key if null
    }
    setSearchParams(updatedParams, { replace: true }); // Update URL without reloading
  };
  useEffect(() => {
    if (selectedSharedUid) {
      // dispatch(setSelectedSharedUid(selectedSharedUid));
      dispatch(setSelectedSite(selectedSharedUid));
      setSelectedSites(
        searchParams
          .get("selectedSites")
          ?.split(",")
          .map((site: any) => ({ value: site, label: site })) || []
      );
      updateURLParams(selectedSharedUid);
    }
    setDataLoaded(false);
    setTableRefresh(false);
    if (search === true && dataShow && selectedSiteUids?.length !== 0) {
      setLoading(true);
      const fetchBusinessInsights = async () => {
        try {
          await BusinessInsightsEndPointLogic.getAllBusinessInsights(
            selectedSiteUids,
            oauthTokenHeader,
            dispatch
          );
        } catch (error: any) {
          store.dispatch(
            setDisplayErrorSnackBar({
              message:
                error?.parsedText ||
                "Error while fetching data. Please try again later.",
              type: "error",
            })
          );
        } finally {
          setInitialLoading(false);
          setLoading(false);
          setDataLoaded(true);
        }
      };
      fetchBusinessInsights();
    } else {
      setInitialLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataShow, selectedSiteUids, oauthTokenHeader]);

  // Effect to update filtered insights whenever businessInsightsList or selectedSiteUids change
  useEffect(() => {
    if (businessInsightsList?.length > 0 && selectedSiteUids?.length > 0) {
      const filtered =
        businessInsightsList.filter((insight: any) =>
          selectedSiteUids.includes(insight?.website_shared_uid)
        ) || [];
      dispatch(setFilteredInsights(filtered));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessInsightsList, selectedSiteUids]);

  useEffect(() => {
    setSelectedOption("Website");
  }, []);

  //-- Function to open modal(snooze/assign/resolve)
  const handleModalOpen = (modalType: string) => {
    dispatch(setOpenModalType(modalType));
  };

  return (
    <>
      {hasViewBusinessInsightsPermission ? (
        <>
          <Paper sx={{ padding: "8px 16px", mb: 2 }}>
            <Stack
              flexDirection={"row"}
              justifyContent={{ xs: "center", md: "space-between" }}
              alignItems="center"
              width={"100%"}
            >
              <Box width={"100%"}>
                <Stack
                  direction="row"
                  spacing={2}
                  alignItems="center"
                  justifyContent={"space-between"}
                >
                  <Typography fontSize={24}>Insights</Typography>
                  <Stack direction="row" spacing={2} alignItems="center">
                    <FormLabel
                      color="secondary"
                      id="demo-row-radio-buttons-group-label"
                    >
                      <Typography
                        fontSize={16}
                        color="#000000DE"
                        marginRight={1}
                      >
                        Fetch by:
                      </Typography>
                    </FormLabel>
                    <RadioGroup
                      row
                      aria-labelledby="demo-row-radio-buttons-group-label"
                      name="row-radio-buttons-group"
                    >
                      <FormControlLabel
                        value="Website"
                        control={
                          <Radio
                            color="secondary"
                            size="small"
                            inputProps={
                              { "data-tracker": "297" } as Record<string, any>
                            }
                          />
                        }
                        checked={selectedOption === "Website"}
                        onChange={() => {
                          setSelectedOption("Website");
                          setSearch(false);
                          setDataShow(false);
                          setSelectedSites([]);
                          setSelectedSiteUids([]);
                        }}
                        label="Website"
                      />
                      <FormControlLabel
                        value="Dimension"
                        disabled={true}
                        control={
                          <Radio
                            color="secondary"
                            size="small"
                            disabled={true}
                            inputProps={
                              { "data-tracker": "298" } as Record<string, any>
                            }
                          />
                        }
                        checked={selectedOption === "Dimension"}
                        onChange={() => {
                          setSelectedOption("Dimension");
                          setSearch(false);
                          setDataShow(false);
                        }}
                        label="Dimension"
                      />
                    </RadioGroup>
                  </Stack>
                </Stack>
              </Box>
            </Stack>
            <Stack mt={1} mb={3} flexDirection={"row"} gap={2}>
              <Stack width={"100%"} zIndex={1000}>
                <Autocomplete
                  componentsProps={{
                    popper: {
                      sx: {
                        zIndex: 100,
                      },
                    },
                  }}
                  data-tracker="300"
                  multiple
                  size="small"
                  fullWidth
                  options={
                    allSitesSelected
                      ? mappedSiteOptions
                      : [SELECT_ALL_OPTION, ...mappedSiteOptions]
                  }
                  getOptionLabel={(option: SiteOption) => option.label || ""}
                  value={selectedSites}
                  isOptionEqualToValue={(
                    option: SiteOption,
                    value: SiteOption
                  ) => option?.value === value?.value}
                  onChange={(event, newValue) => {
                    handleSelectSites(event, newValue);
                  }}
                  onInputChange={(_, __, reason) => {
                    if (reason === "clear") {
                      const params = new URLSearchParams(location.search);
                      params.delete("filters");
                      setSearchParams(params);
                      setSearch(false);
                      setDataShow(false);
                      setSelectedSites([]);
                      setSelectedSiteUids([]);
                      setSearchTerm("");
                    }
                  }}
                  filterOptions={(options, { inputValue }) => {
                    const searchTerm = inputValue.toLowerCase();
                    return options.filter((option) =>
                      option.label.toLowerCase().includes(searchTerm)
                    );
                  }}
                  filterSelectedOptions
                  renderTags={(value, getTagProps) =>
                    value.map((option: any, index) => (
                      <Chip
                        {...getTagProps({ index })}
                        key={option?.value}
                        label={option?.label}
                        sx={{
                          backgroundColor: "#7B1FA2",
                          color: "#ffffff",
                          height: "24px",
                        }}
                        deleteIcon={
                          <CancelIcon
                            sx={{
                              color: "rgba(255, 255, 255, 0.8) !important",
                              fontSize: "16px !important",
                            }}
                          />
                        }
                      />
                    ))
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Search site name or choose from the list"
                      label="Select Website"
                      color="secondary"
                      sx={{ height: 40 }}
                      slotProps={{
                        inputLabel: {
                          shrink: true,
                        },
                      }}
                    />
                  )}
                  renderOption={(props, option) => (
                    <li
                      {...props}
                      style={{
                        backgroundColor:
                          option.value === SELECT_ALL_OPTION.value
                            ? "#9C27B00A"
                            : "inherit",
                        color:
                          option.value === SELECT_ALL_OPTION.value
                            ? "#9C27B0"
                            : "inherit",
                        fontWeight:
                          option.value === SELECT_ALL_OPTION.value
                            ? "500"
                            : "normal",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = "#9C27B00A";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor =
                          option.value === SELECT_ALL_OPTION.value
                            ? "#9C27B00A"
                            : "inherit";
                      }}
                    >
                      {option.label}
                    </li>
                  )}
                />
              </Stack>
            </Stack>
          </Paper>
          {initialLoading ? (
            <SimpleBackdrop />
          ) : selectedSiteUids?.length !== 0 ? (
            filteredInsights?.length !== 0 || !initialLoading ? (
              <Paper>
                <InsightsFilters
                  onResolveIconClick={() => handleModalOpen("resolve")}
                  onAssignIconClick={() => handleModalOpen("assign")}
                  onSnoozeIconClick={() => handleModalOpen("snooze")}
                />
                <InsightsTable loading={loading} />
              </Paper>
            ) : (
              <Paper>
                {loading || filteredInsights?.length <= 0 ? (
                  <>
                    <InsightsFilters
                      onResolveIconClick={() => handleModalOpen("resolve")}
                      onAssignIconClick={() => handleModalOpen("assign")}
                      onSnoozeIconClick={() => handleModalOpen("snooze")}
                    />
                    <TableContainer className="" component={Paper}>
                      <Table size="small" stickyHeader>
                        <TableBody>
                          <TableRow
                            sx={{
                              "& > *": {
                                borderBottom:
                                  "1px solid rgba(224, 224, 224, 1) !important",
                              },
                            }}
                          >
                            <TableCell colSpan={6} align="center">
                              <Typography variant="body1">
                                No Data Available
                              </Typography>
                            </TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                      <Stack
                        spacing={1}
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                      >
                        <TablePagination
                          rowsPerPageOptions={[]}
                          component="div"
                          count={filteredInsights?.length || 0}
                          rowsPerPage={0}
                          page={0}
                          onPageChange={() => {}}
                          onRowsPerPageChange={() => {}}
                          sx={{
                            ".css-16c50h-MuiInputBase-root-MuiTablePagination-select,.MuiTablePagination-selectLabel":
                              {
                                display: "none",
                              },
                            ".MuiTablePagination-actions": {
                              display: "none",
                            },
                            ".MuiTablePagination-toolbar": {
                              paddingLeft: "16px",
                            },
                          }}
                        />
                        <Pagination
                          count={Math.ceil(filteredInsights?.length)}
                          page={Number(searchParams.get("page")) + 1 || 1}
                          showFirstButton
                          showLastButton
                          className="pagination-button"
                          color="secondary"
                          size="small"
                          sx={{
                            ".css-wjh20t-MuiPagination-ul": {
                              justifyContent: "center",
                            },
                          }}
                        />
                      </Stack>
                    </TableContainer>
                  </>
                ) : null}
              </Paper>
            )
          ) : null}
        </>
      ) : (
        <Page404 />
      )}
      <div data-tracker="446">
        {selectedSiteUids?.length > 0 ? (
          <FloatingActionItems
            selectedSiteFloatingAction={selectedSiteUids[0]}
            selectedDimensionsFloatingAction={""}
            trackIdFloatingAction={"434"}
          />
        ) : (
          ""
        )}
      </div>
    </>
  );
};

export default Insights;
